import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setErr("Please accept the terms and privacy policy"); return; }
    setErr(""); setLoading(true);
    try {
      const data = await register(form.name, form.email, form.password);
      if (data.email_delivery === "dev_not_configured") {
        toast("Account created. Email delivery is not configured (dev mode) — you can verify instantly from your dashboard.");
      } else {
        toast.success("Account created. Check your email to verify.");
      }
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center py-16 px-5" data-testid="register-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#E3E8E8] shadow-soft p-8">
        <Link to="/"><Logo /></Link>
        <h1 className="font-display font-extrabold text-3xl text-[#0B1733] mt-6">Join free</h1>
        <p className="text-[#4A5568] mt-1 text-sm">Post jobs, join the community and compare quotes.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name-input" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email-input" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="register-password-input" className="mt-1.5 h-11" placeholder="At least 6 characters" />
          </div>
          <label className="flex items-start gap-2.5 text-sm text-[#4A5568] cursor-pointer">
            <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="register-terms-checkbox" className="mt-0.5" />
            <span>I agree to the <Link to="/terms" className="text-[#007F83] hover:underline">Terms</Link> and <Link to="/privacy" className="text-[#007F83] hover:underline">Privacy Policy</Link>.</span>
          </label>
          {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
          <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full h-11 rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">
            {loading ? "Creating..." : "Create account"}
          </Button>
        </form>
        <p className="mt-5 text-sm text-center text-[#4A5568]">
          Already have an account? <Link to="/login" className="text-[#007F83] font-medium hover:underline" data-testid="to-login-link">Log in</Link>
        </p>
      </div>
    </div>
  );
}
